import { type FC, useState } from 'react'
import { Button } from '../../../../../components/Button/Button'
import { EventTypeAccess, type SchedulingEventType } from '../../../../../models/Scheduling'
import { formatRelativeDateTime } from '../../../../../common/dateUtils'
import { UsedByModal } from './UsedByModal'

interface EditorActionsProps {
  eventType: SchedulingEventType
  disabled: boolean
  canEdit: boolean
  onEnableEditing: () => void
  onDuplicate: () => void
  onDelete: () => void
}

export const EditorActions: FC<EditorActionsProps> = ({ eventType, disabled, canEdit, onEnableEditing, onDuplicate, onDelete }) => {
  const [isUsedByOpen, setIsUsedByOpen] = useState(false)

  return (
    <div className='d-flex flex-column gap-3'>
      <div className='d-flex flex-column zocks-gap-0.5 p-3 border rounded-3 zocks-border-color-gray-300 zocks-bg-color-white'>
        <div className='d-flex align-items-center'>
          <span className='paragraph-small zocks-text-gray-600 flex-grow-1'>Access</span>
          <span className='w-50 d-flex align-items-center'>
            {eventType.access === EventTypeAccess.ACCOUNT ? (
              <span className='d-inline-flex align-items-center zocks-gap-0.25 badge rounded-2 zocks-bg-color-success-background zocks-text-gray-900'>
                <i className='bi bi-people-fill' />
                Account
              </span>
            ) : (
              <span className='d-inline-flex align-items-center zocks-gap-0.25 badge rounded-2 zocks-bg-color-warning zocks-text-gray-900'>
                <i className='bi bi-person-fill' />
                Personal
              </span>
            )}
          </span>
        </div>
        <div className='d-flex align-items-center'>
          <span className='paragraph-small zocks-text-gray-600 flex-grow-1'>Used by</span>
          <span className='w-50 d-flex align-items-center zocks-gap-0.5 paragraph-small'>
            <span>
              <span className='bold'>{eventType.usedByCount}</span>{' '}
              <span className='zocks-text-gray-700'>{eventType.usedByCount === 1 ? 'user' : 'users'}</span>
            </span>
            <button
              type='button'
              className='btn btn-link p-0 border-0 paragraph-small text-decoration-none zocks-text-primary'
              onClick={() => setIsUsedByOpen(true)}
            >
              See list
            </button>
          </span>
        </div>
        {eventType.updatedAt ? (
          <div className='d-flex align-items-center'>
            <span className='paragraph-small zocks-text-gray-600 flex-grow-1'>Last edited</span>
            <span className='w-50 paragraph-small'>{formatRelativeDateTime(new Date(eventType.updatedAt))}</span>
          </div>
        ) : null}
      </div>
      <div className='d-flex flex-column zocks-gap-0.5'>
        {disabled && canEdit ? (
          <Button variant='primary' className='justify-content-start' onClick={onEnableEditing}>
            <i className='bi bi-pencil me-1' />
            Enable editing
          </Button>
        ) : null}
        {!disabled ? (
          <>
            <Button variant='grayButton' className='justify-content-start' onClick={onDuplicate}>
              <i className='bi bi-copy me-1' />
              Duplicate event type
            </Button>
            <Button variant='grayButton' className='justify-content-start' onClick={onDelete}>
              <span className='d-flex align-items-center zocks-gap-0.625 zocks-text-danger'>
                <i className='bi bi-trash me-1' />
                Delete event type
              </span>
            </Button>
          </>
        ) : null}
      </div>
      {isUsedByOpen ? <UsedByModal eventTypeId={eventType.id} eventTypeSlug={eventType.slug} onClose={() => setIsUsedByOpen(false)} /> : null}
    </div>
  )
}
